/**
 * Client-side image quality assessment using Canvas API.
 *
 * Evaluates uploaded or captured document images for blur, brightness,
 * contrast, and resolution. Returns a structured quality result with
 * an overall score, individual metrics, and actionable issues.
 *
 * Blur detection uses a Laplacian-like 3x3 convolution kernel and
 * measures the variance of the response -- low variance indicates a
 * blurry image. This mirrors the OpenCV `cv2.Laplacian` variance
 * technique used in the Python reference implementation.
 *
 * @module hooks/useImageQuality
 */

import { useCallback, useState } from 'react';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export interface ImageQualityResult {
  /** Weighted overall score from 0 (worst) to 1 (best). */
  overallScore: number;
  /** True when the overall score meets the acceptance threshold (>= 0.3). */
  isAcceptable: boolean;
  /** Detected issues sorted by severity (rejections first). */
  issues: ImageQualityIssue[];
  /** Raw metric values from each quality check. */
  metrics: {
    /** Laplacian variance proxy -- higher is sharper. */
    blurScore: number;
    /** Average luminance (0-255). */
    brightnessScore: number;
    /** Standard deviation of luminance -- higher is more contrast. */
    contrastScore: number;
    /** Original image dimensions. */
    resolution: { width: number; height: number };
    /** Ratio of pixels with luminance > 245 to total pixels (0-1). */
    glareRatio: number;
  };
}

export interface ImageQualityIssue {
  type: 'blur' | 'dark' | 'bright' | 'low_resolution' | 'low_contrast' | 'glare';
  severity: 'warning' | 'rejection';
  message: string;
}

// ---------------------------------------------------------------------------
// Thresholds (tuned to match reference Python implementation)
// ---------------------------------------------------------------------------

/** Laplacian variance below this is a hard rejection. */
const BLUR_REJECT = 10;
/** Laplacian variance below this is a warning. */
const BLUR_WARN = 60;

/** Average luminance below this triggers "too dark". */
const DARK_THRESHOLD = 50;
/** Average luminance above this triggers "too bright". */
const BRIGHT_THRESHOLD = 200;

/** Luminance std-dev below this triggers "low contrast". */
const CONTRAST_THRESHOLD = 30;

/** Minimum shortest side for acceptable quality. */
const RESOLUTION_ACCEPT = 1000;
/** Minimum shortest side before warning. */
const RESOLUTION_WARN = 600;

/** Glare ratio above this is a hard rejection (> 25%). */
const GLARE_REJECT = 0.25;
/** Glare ratio above this triggers a warning (> 5%). */
const GLARE_WARN = 0.05;
/** Luminance threshold for counting "high brightness" pixels. */
const GLARE_PIXEL_THRESHOLD = 245;

// ---------------------------------------------------------------------------
// Canvas helpers (pure functions -- no React dependency)
// ---------------------------------------------------------------------------

/**
 * Load a File into an HTMLImageElement and return it along with the
 * underlying ImageData via an offscreen canvas.
 */
function loadImageData(file: File): Promise<{ img: HTMLImageElement; data: ImageData }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        URL.revokeObjectURL(url);
        reject(new Error('Canvas 2D context unavailable'));
        return;
      }

      ctx.drawImage(img, 0, 0);
      const data = ctx.getImageData(0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      resolve({ img, data });
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image'));
    };

    img.src = url;
  });
}

/**
 * Convert RGBA ImageData to a single-channel grayscale Float64Array
 * using the BT.601 luminance formula: 0.299*R + 0.587*G + 0.114*B.
 */
function toGrayscale(data: ImageData): Float64Array {
  const { width, height, data: px } = data;
  const gray = new Float64Array(width * height);
  for (let i = 0; i < gray.length; i++) {
    const o = i * 4;
    gray[i] = 0.299 * px[o] + 0.587 * px[o + 1] + 0.114 * px[o + 2];
  }
  return gray;
}

/**
 * Compute the Laplacian-like response variance using a 3x3 kernel:
 *
 *   -1  -1  -1
 *   -1   8  -1
 *   -1  -1  -1
 *
 * The kernel highlights edges; a blurry image produces low variance.
 */
function laplacianVariance(gray: Float64Array, width: number, height: number): number {
  // Skip the 1px border to avoid boundary checks
  const len = (width - 2) * (height - 2);
  if (len <= 0) return 0;

  let sum = 0;
  let sumSq = 0;

  for (let y = 1; y < height - 1; y++) {
    for (let x = 1; x < width - 1; x++) {
      const idx = y * width + x;
      const lap =
        8 * gray[idx] -
        gray[idx - width - 1] -
        gray[idx - width] -
        gray[idx - width + 1] -
        gray[idx - 1] -
        gray[idx + 1] -
        gray[idx + width - 1] -
        gray[idx + width] -
        gray[idx + width + 1];

      sum += lap;
      sumSq += lap * lap;
    }
  }

  const mean = sum / len;
  return sumSq / len - mean * mean;
}

/**
 * Compute the mean and standard deviation of a Float64Array.
 */
function meanAndStd(values: Float64Array): { mean: number; std: number } {
  const n = values.length;
  if (n === 0) return { mean: 0, std: 0 };

  let sum = 0;
  for (let i = 0; i < n; i++) sum += values[i];
  const mean = sum / n;

  let sumSq = 0;
  for (let i = 0; i < n; i++) {
    const d = values[i] - mean;
    sumSq += d * d;
  }
  return { mean, std: Math.sqrt(sumSq / n) };
}

/**
 * Run all quality checks and return a structured result.
 */
function assess(data: ImageData): ImageQualityResult {
  const { width, height } = data;
  const gray = toGrayscale(data);
  const { mean: brightness, std: contrast } = meanAndStd(gray);

  // Blur detection via Laplacian variance
  const blurVar = laplacianVariance(gray, width, height);

  // Glare detection: count pixels with luminance above the threshold
  const totalPixels = gray.length;
  let highBrightnessCount = 0;
  for (let i = 0; i < totalPixels; i++) {
    if (gray[i] > GLARE_PIXEL_THRESHOLD) {
      highBrightnessCount++;
    }
  }
  const glareRatio = totalPixels > 0 ? highBrightnessCount / totalPixels : 0;

  // Normalise individual metrics to a 0-1 scale for the overall score.
  // Each normalisation is a simple linear clamp.

  // Blur: 0 at threshold, 1 when comfortably above warning threshold
  const blurNorm = Math.min(1, Math.max(0, (blurVar - BLUR_REJECT) / (BLUR_WARN * 3 - BLUR_REJECT)));

  // Brightness: 1.0 at 128, dropping toward 0 at extremes
  const brightDist = Math.abs(brightness - 128);
  const brightnessNorm = Math.max(0, 1 - brightDist / 128);

  // Contrast: 0 at 0, 1 at 60+
  const contrastNorm = Math.min(1, Math.max(0, contrast / 60));

  // Resolution: 0 below warn, 1 at accept
  const minDim = Math.min(width, height);
  const resNorm = Math.min(1, Math.max(0, (minDim - RESOLUTION_WARN) / (RESOLUTION_ACCEPT - RESOLUTION_WARN)));

  // Glare: 1.0 when no glare, dropping to 0 as glare increases (clamped)
  const glareScore = Math.min(1, Math.max(0, 1 - glareRatio * 4));

  // Weighted overall score (weights sum to 1.0)
  const overallScore =
    0.30 * blurNorm +
    0.20 * brightnessNorm +
    0.10 * contrastNorm +
    0.20 * resNorm +
    0.20 * glareScore;

  // Collect issues
  const issues: ImageQualityIssue[] = [];

  // Blur issues
  if (blurVar < BLUR_REJECT) {
    issues.push({
      type: 'blur',
      severity: 'rejection',
      message: 'Image is extremely blurry and unreadable. Please retake with the camera in focus.',
    });
  } else if (blurVar < BLUR_WARN) {
    issues.push({
      type: 'blur',
      severity: 'warning',
      message: 'Image appears slightly blurry. Text may be difficult to read.',
    });
  }

  // Brightness issues
  if (brightness < DARK_THRESHOLD) {
    issues.push({
      type: 'dark',
      severity: brightness < 30 ? 'rejection' : 'warning',
      message: 'Image is too dark. Please retake in a well-lit environment.',
    });
  } else if (brightness > BRIGHT_THRESHOLD) {
    issues.push({
      type: 'bright',
      severity: brightness > 230 ? 'rejection' : 'warning',
      message: 'Image is too bright or has glare. Please avoid direct light or flash.',
    });
  }

  // Glare issues
  if (glareRatio > GLARE_REJECT) {
    issues.push({
      type: 'glare',
      severity: 'rejection',
      message: 'Excessive glare detected. Please retake without flash and avoid reflective surfaces.',
    });
  } else if (glareRatio > GLARE_WARN) {
    issues.push({
      type: 'glare',
      severity: 'warning',
      message: 'Some glare detected. This may affect readability of certain areas.',
    });
  }

  // Contrast issues
  if (contrast < CONTRAST_THRESHOLD) {
    issues.push({
      type: 'low_contrast',
      severity: contrast < 15 ? 'rejection' : 'warning',
      message: 'Image has very low contrast. Ensure the document is clearly visible.',
    });
  }

  // Resolution issues
  if (minDim < RESOLUTION_WARN) {
    issues.push({
      type: 'low_resolution',
      severity: 'rejection',
      message: `Image resolution is too low (${width}x${height}). Please use a higher resolution camera or move closer.`,
    });
  } else if (minDim < RESOLUTION_ACCEPT) {
    issues.push({
      type: 'low_resolution',
      severity: 'warning',
      message: `Image resolution is below recommended (${width}x${height}). OCR accuracy may be reduced.`,
    });
  }

  // Sort: rejections first
  issues.sort((a, b) => {
    if (a.severity === b.severity) return 0;
    return a.severity === 'rejection' ? -1 : 1;
  });

  const isAcceptable = overallScore >= 0.3;

  return {
    overallScore,
    isAcceptable,
    issues,
    metrics: {
      blurScore: Math.round(blurVar * 100) / 100,
      brightnessScore: Math.round(brightness * 100) / 100,
      contrastScore: Math.round(contrast * 100) / 100,
      resolution: { width, height },
      glareRatio: Math.round(glareRatio * 10000) / 10000,
    },
  };
}

// ---------------------------------------------------------------------------
// React hook
// ---------------------------------------------------------------------------

export function useImageQuality(): {
  assessImage: (file: File) => Promise<ImageQualityResult>;
  isAssessing: boolean;
} {
  const [isAssessing, setIsAssessing] = useState(false);

  const assessImage = useCallback(async (file: File): Promise<ImageQualityResult> => {
    setIsAssessing(true);
    try {
      const { data } = await loadImageData(file);
      return assess(data);
    } finally {
      setIsAssessing(false);
    }
  }, []);

  return { assessImage, isAssessing };
}
